/**
 * rebootConfirm -- what the in-page reboot confirmation on the device sheet says, per device kind.
 *
 * WHY (UI review 2026-10-17): the sheet's Reboot button posted POST /devices/<serial>/reboot on
 * the FIRST click, with no confirmation and no outage warning. A reboot is a real outage:
 *   * an access point is off the air for roughly 2-4 minutes (radios + owgw reconnect), and
 *     every client on it drops;
 *   * a switch takes down everything behind it, PoE-powered APs included;
 *   * a whitebox GATEWAY (QOTOM J1900-class x86) can HANG on a remote soft reboot and never
 *     come back -- 2026-10-07 an office went dark that way and a watchdog stop did not reset it.
 *     Only a person on site (power cycle) recovers it, so the confirmation requires the operator
 *     to tick "someone is on site" before the button arms.
 *
 * The confirmation is IN-PAGE (never window.confirm: it is unstyled, unreadable in screenshots
 * and manuals, and invisible to the e2e suite). Role gating (RoleGate site_operator, mirroring
 * @require("site_operator") on the route) and the server-side audit row are unchanged.
 *
 * No React, no fetch: unit-tested in rebootConfirm.test.ts (`npm run test:unit`).
 */

/**
 * OP#1929 (interim, owner-approved 2026-10-19): Reboot from Meridian is NOT available. The API
 * used to record SENT without anything reaching the device (dispatch only emitted to a Socket.IO
 * room no uCentral/owgw device joins); it now answers 501 with this same reason. The device-sheet
 * button is disabled for EVERY kind with this text as its tooltip. Flip to true only together
 * with the real owgw reboot (and the QOTOM soft-reboot hang safeguards).
 */
export const REBOOT_FROM_MERIDIAN_AVAILABLE: boolean = false;

/** Tooltip + visible reason on the disabled Reboot button (matches the API's 501 error). */
export const REBOOT_UNAVAILABLE_REASON =
  "Reboot from Meridian is not available yet (OP #1929). Use the device's own CLI or power-cycle on site.";

export interface RebootWarning {
  /** Headline naming the device, e.g. "Reboot Lobby-AP-01?". */
  title: string;
  /** What the outage looks like for this kind of device. */
  impact: string;
  /** "critical" for gateways (may not come back), "warn" otherwise. */
  tone: "warn" | "critical";
  /** When set, the confirm button stays disabled until this checkbox is ticked. */
  ackLabel: string | null;
  /** Label of the button that actually sends the reboot. */
  confirmLabel: string;
}

/** Display name for the confirmation: the device name, falling back to its serial. */
function deviceLabel(name: string | null | undefined, serial: string): string {
  const n = (name ?? "").trim();
  return n && n !== serial ? `${n} (${serial})` : serial;
}

/**
 * The warning for one device. `kind` is Device.kind ("access_point" | "switch" | "gateway" | ...).
 * Unknown kinds get the generic outage line rather than the AP timing, which would be a guess.
 */
export function rebootWarning(
  kind: string | null | undefined,
  name: string | null | undefined,
  serial: string,
): RebootWarning {
  const label = deviceLabel(name, serial);
  const title = `Reboot ${label}?`;
  switch (kind) {
    case "access_point":
      return {
        title,
        impact:
          "The access point goes offline for about 2-4 minutes. Every client connected to it " +
          "drops and has to reconnect.",
        tone: "warn",
        ackLabel: null,
        confirmLabel: "Reboot access point",
      };
    case "switch":
      return {
        title,
        impact:
          "The switch and everything behind it go offline for a few minutes, including access " +
          "points and other devices it powers over PoE.",
        tone: "warn",
        ackLabel: null,
        confirmLabel: "Reboot switch",
      };
    case "gateway":
      return {
        title,
        impact:
          "The whole site loses internet while the gateway restarts. A remote reboot of a " +
          "QOTOM / J1900-class gateway can hang and never come back: only a power cycle on " +
          "site recovers it. Reboot a gateway only with someone on site.",
        tone: "critical",
        ackLabel: "Someone is on site and can power-cycle the gateway if it does not come back.",
        confirmLabel: "Reboot gateway",
      };
    default:
      return {
        title,
        impact: "The device goes offline while it restarts, and anything depending on it drops.",
        tone: "warn",
        ackLabel: null,
        confirmLabel: "Reboot device",
      };
  }
}

/** True when the confirm button may send the reboot (the gateway ack, when required, is ticked). */
export function canConfirmReboot(w: RebootWarning, acked: boolean): boolean {
  return w.ackLabel === null || acked;
}
